'use client';

import { useEffect } from 'react';
import { usePreviewContext } from '../../context/PreviewContext';
import { usePreviewLimits } from './PreviewLimitContext';

export interface PreviewLimitIssue {
    id?: string;
    component: string;
    field: string;
    actualLength: number;
    limit: number;
    source?: string;
    settingOwnerId?: string;
}

export interface PreviewLimitParams {
    id?: string;
    component: string;
    field: string;
    value: string;
    limit: number;
    source?: string;
    settingOwnerId?: string;
    onIssue?: (issue: PreviewLimitIssue) => void;
}

function buildPreviewLimitIssue(
    params: PreviewLimitParams
): PreviewLimitIssue | null {
    if (typeof params.limit !== 'number' || !Number.isFinite(params.limit)) {
        return null;
    }

    const actualLength = (params.value || '').length;
    if (actualLength <= params.limit) return null;

    return {
        id: params.id,
        component: params.component,
        field: params.field,
        actualLength,
        limit: params.limit,
        source: params.source,
        settingOwnerId: params.settingOwnerId,
    };
}

function reportPreviewLimitIssue(
    issue: PreviewLimitIssue,
    params: PreviewLimitParams,
    previewLimitsContext: ReturnType<typeof usePreviewLimits>
) {
    params.onIssue?.(issue);
    previewLimitsContext?.report?.(issue);
}

export function usePreviewLimitCheck(params: PreviewLimitParams) {
    const { isPreview } = usePreviewContext();
    const previewLimitsContext = usePreviewLimits();

    useEffect(() => {
        if (!isPreview) return;

        const issue = buildPreviewLimitIssue(params);
        if (!issue) return;

        reportPreviewLimitIssue(issue, params, previewLimitsContext);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isPreview,
        previewLimitsContext,
        params.id,
        params.component,
        params.field,
        params.value,
        params.limit,
        params.source,
        params.settingOwnerId,
        params.onIssue,
    ]);
}

export function usePreviewLimitChecks(
    entries?: PreviewLimitParams[]
) {
    const { isPreview } = usePreviewContext();
    const previewLimitsContext = usePreviewLimits();

    useEffect(() => {
        if (!isPreview || !entries?.length) return;

        for (const entry of entries) {
            const issue = buildPreviewLimitIssue(entry);
            if (!issue) continue;

            reportPreviewLimitIssue(issue, entry, previewLimitsContext);
        }
    }, [isPreview, previewLimitsContext, entries]);
}
